/**
 * ============================================================================
 * TEXTAREA COMPONENT - 100% Ready
 * ============================================================================
 */

import React, { forwardRef } from 'react';

/**
 * Textarea Component
 * @param {Object} props
 * @param {string} props.value - Textarea value
 * @param {Function} props.onChange - Change handler
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.error - Error message
 * @param {string} props.className - Additional classes
 * @param {boolean} props.required - Required field
 * @param {string} props.label - Label text
 * @param {number} props.rows - Number of rows
 * @param {string} props.id - Textarea ID
 * @param {string} props.name - Textarea name
 * @param {number} props.maxLength - Maximum length
 * @returns {JSX.Element}
 */
const Textarea = forwardRef(
  (
    {
      value,
      onChange,
      placeholder,
      error,
      className = '',
      required = false,
      label,
      rows = 4,
      id,
      name,
      maxLength,
      ...props
    },
    ref
  ) => {
    const baseClasses = `
      w-full px-4 py-2.5
      border rounded-xl
      bg-white dark:bg-gray-800
      text-gray-900 dark:text-white
      placeholder-gray-400 dark:placeholder-gray-500
      focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500
      transition-all duration-200
      resize-y
    `;

    const errorClasses = error
      ? 'border-red-500 focus:ring-red-500/20 focus:border-red-500'
      : 'border-gray-300 dark:border-gray-600';

    const classes = `${baseClasses} ${errorClasses} ${className}`.trim();

    const textareaId = id || name;
    const currentLength = value?.length || 0;

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={textareaId}
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          rows={rows}
          maxLength={maxLength}
          className={classes}
          aria-invalid={!!error}
          aria-describedby={
            error ? `${textareaId}-error` : maxLength ? `${textareaId}-help` : undefined
          }
          {...props}
        />
        {(error || maxLength) && (
          <div className="mt-1 flex items-center justify-between">
            {error && (
              <p
                id={`${textareaId}-error`}
                className="text-sm text-red-600 dark:text-red-400"
                role="alert"
              >
                {error}
              </p>
            )}
            {maxLength && (
              <p
                id={`${textareaId}-help`}
                className={`text-xs ml-auto ${
                  currentLength >= maxLength
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {currentLength}/{maxLength}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export default Textarea;
